/** @jsxImportSource @emotion/react */
import { Formik, type FormikHelpers } from 'formik';
import _ from 'lodash';
import { Pages } from 'modules/navigation/domain/enums/pages';
import validationSchema from 'modules/settings/domain/validators/social-net.validator';
import React, { useState } from 'react';
import { usePreventNavigation } from 'shared/hooks';
import UDActions from 'ud-ui/components/actions/ud-actions';
import InstagramIcon from 'ud-ui/components/icon/social-net/instagram-icon';
import TelegramIcon from 'ud-ui/components/icon/social-net/telegram-icon';
import VkIcon from 'ud-ui/components/icon/social-net/vk-icon';
import VkVideoIcon from 'ud-ui/components/icon/social-net/vk-video-icon';
import YoutubeIcon from 'ud-ui/components/icon/social-net/youtube-icon';
import type { DivProps } from 'typings';
import Field from '../field/field';
import Header from '../header/header';
import * as S from './styles';

export interface InitialValues {
  website?: string;
  vk?: string;
  'vk-video'?: string;
  telegram?: string;
  youtube?: string;
  instagram?: string;
}

interface IProps extends Omit<DivProps, 'onSubmit'> {
  initialValues: InitialValues;
  onSubmit?: (values: InitialValues, formikHelpers: FormikHelpers<InitialValues>) => void | Promise<any>;
  loading?: boolean;
  loader?: boolean;
}

function SocialNetForm(props: IProps) {
  const { initialValues, onSubmit, loading, loader, ...rest } = props;

  const [isFormDirty, setIsFormDirty] = useState(false);
  const [leavingConfirmed, setLeavingConfirmed] = useState(false);

  const { PreventNavigationModal } = usePreventNavigation({
    shouldPrevent: isFormDirty && !leavingConfirmed,
    onConfirm: () => {
      setLeavingConfirmed(true);
      setIsFormDirty(false);
    },
    defaultPath: Pages.SETTINGS.INDEX,
  });

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize={true}
      onSubmit={(values, formikHelpers) => {
        onSubmit?.(values, formikHelpers);
        formikHelpers.resetForm({ values }); // делаем текущие значения базой
        setIsFormDirty(false);
      }}
      validate={(values) => {
        const dirty = !_.isEqual(values, initialValues);
        if (dirty !== isFormDirty && !leavingConfirmed) {
          setIsFormDirty(dirty);
        }
        return {};
      }}
    >
      {({ handleSubmit, isSubmitting, setValues }) => {
        return (
          <form onSubmit={handleSubmit}>
            <S.MainBlock {...rest}>
              <S.Category>
                <Header className="mb-2" tip="Укажите актуальный адрес сайта. Пример формата: https://imgameapp.ru">
                  Адрес сайта
                </Header>
                <S.Fields>
                  <Field name="website" placeholder="Вставьте ссылку на сайт" loading={loader} />
                </S.Fields>
              </S.Category>
              <S.Category>
                <Header
                  className="mb-2"
                  tip="Обязательно укажите рабочую ссылку на конкретную социальную сеть. Если ссылка будет неправильная, то она не будет работать. Пример: https://vkvideo.ru/eliga"
                >
                  Социальные сети
                </Header>
                <S.Fields>
                  <Field name="vk" placeholder="Вставте ссылку" icon={<VkIcon />} label="ВКонтакте" loading={loader} />
                  <Field name="vk-video" placeholder="Вставте ссылку" icon={<VkVideoIcon />} label="ВКвидео" loading={loader} />
                  <Field name="telegram" placeholder="Вставте ссылку" icon={<TelegramIcon />} label="Telegram" loading={loader} />
                  <Field name="youtube" placeholder="Вставте ссылку" icon={<YoutubeIcon />} label="YouTube" loading={loader} />
                  <Field name="instagram" placeholder="Вставте ссылку" icon={<InstagramIcon />} label="Instagram" loading={loader} />
                </S.Fields>
              </S.Category>
            </S.MainBlock>
            <UDActions
              submitText="Сохранить"
              cancelText="Отменить"
              onCancel={() => setValues(initialValues)}
              loading={(isSubmitting || loading) ?? false}
            />

            <PreventNavigationModal />
          </form>
        );
      }}
    </Formik>
  );
}

export default SocialNetForm;
